import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
  Select,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Toolbar,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Refresh,
  ArrowUpward,
  ArrowDownward,
  FilterListOff,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { rentalTypeApi } from '../services/api';
import { RentalTypeRecord, RentalTypeSaveRequest } from '../types/rentalType';
import { TableFilterCell } from '../components/TableFilterCell';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';

const createEmptyForm = (): RentalTypeSaveRequest => ({
  name: '',
  kind: 'PERSON',
  active: true,
});

export const RentalTypesManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRentalType, setSelectedRentalType] = useState<RentalTypeRecord | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [formData, setFormData] = useState<RentalTypeSaveRequest>(createEmptyForm());

  const [columnFilters, setColumnFilters] = useState({
    name: '',
    kind: '',
    status: '',
  });
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const { data: rentalTypes = [], isLoading, refetch } = useQuery('rentalTypes', rentalTypeApi.getAll);

  const isSystemDefault = Boolean(selectedRentalType?.isSystemDefault);
  // Person must always exist as a rental type, so it stays locked. Company doesn't -
  // most clients only rent to individuals - so editing it is allowed; saving creates
  // this company's own copy instead of touching the shared default (see backend).
  const isLockedDefault = isSystemDefault && selectedRentalType?.kind === 'PERSON';

  const resetForm = () => {
    setFormData(createEmptyForm());
    setSelectedRentalType(null);
    setIsCreating(false);
    setEditMode(false);
  };

  const loadIntoEditor = (rentalType: RentalTypeRecord) => {
    setSelectedRentalType(rentalType);
    setFormData({
      name: rentalType.name,
      kind: rentalType.kind,
      active: rentalType.active,
    });
    setIsCreating(false);
    setEditMode(true);
  };

  const createMutation = useMutation((data: RentalTypeSaveRequest) => rentalTypeApi.create(data), {
    onSuccess: (created) => {
      queryClient.invalidateQueries('rentalTypes');
      toast.success(t('rentalTypes.messages.created', 'Rental type created.'));
      loadIntoEditor(created);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('rentalTypes.messages.createFailed', 'Failed to create rental type.'));
    },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<RentalTypeSaveRequest> }) => rentalTypeApi.update(id, data),
    {
      onSuccess: (updated) => {
        queryClient.invalidateQueries('rentalTypes');
        toast.success(t('rentalTypes.messages.updated', 'Rental type updated.'));
        loadIntoEditor(updated);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('rentalTypes.messages.updateFailed', 'Failed to update rental type.'));
      },
    }
  );

  const deleteMutation = useMutation((id: string) => rentalTypeApi.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('rentalTypes');
      toast.success(t('rentalTypes.messages.deleted', 'Rental type deleted.'));
      setOpenDeleteDialog(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('rentalTypes.messages.deleteFailed', 'Failed to delete rental type.'));
    },
  });

  const handleNew = () => {
    resetForm();
    setIsCreating(true);
    setEditMode(true);
  };

  const handleRowClick = (rentalType: RentalTypeRecord) => {
    if (selectedRentalType?.id === rentalType.id && !isCreating) {
      return;
    }
    loadIntoEditor(rentalType);
  };

  const handleDelete = () => {
    if (selectedRentalType) {
      setOpenDeleteDialog(true);
    }
  };

  const handleConfirmDelete = () => {
    if (selectedRentalType) {
      deleteMutation.mutate(selectedRentalType.id.toString());
    }
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      toast.error(t('rentalTypes.validation.nameRequired', 'Rental type name is required.'));
      return;
    }

    if (isCreating) {
      createMutation.mutate(formData);
    } else if (editMode && selectedRentalType) {
      updateMutation.mutate({ id: selectedRentalType.id.toString(), data: formData });
    }
  };

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [column]: value }));
    setPage(0);
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return prev.direction === 'asc' ? { key, direction: 'desc' } : null;
      }
      return { key, direction: 'asc' };
    });
  };

  const filteredRentalTypes = rentalTypes.filter((rentalType) => {
    const statusText = rentalType.active ? 'active' : 'inactive';
    return (
      (!columnFilters.name || rentalType.name.toLowerCase().includes(columnFilters.name.toLowerCase())) &&
      (!columnFilters.kind || rentalType.kind.toLowerCase().includes(columnFilters.kind.toLowerCase())) &&
      (!columnFilters.status || statusText.includes(columnFilters.status.toLowerCase()))
    );
  });

  const sortedRentalTypes = [...filteredRentalTypes].sort((a, b) => {
    if (!sortConfig) return 0;
    let aValue: any;
    let bValue: any;
    switch (sortConfig.key) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'kind':
        aValue = a.kind;
        bValue = b.kind;
        break;
      case 'status':
        aValue = a.active ? 1 : 0;
        bValue = b.active ? 1 : 0;
        break;
      default:
        return 0;
    }
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sortedRentalTypes.length / rowsPerPage));
  const paginatedRentalTypes = sortedRentalTypes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1100,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          width: '100%',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ pl: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 3 }, minHeight: '48px !important', py: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNew}
                size="small"
                sx={{ minWidth: 'auto', px: 2, py: 0.75, textTransform: 'none', fontSize: '0.875rem' }}
              >
                {t('common.new', 'New')}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={(!isCreating && !editMode) || isLockedDefault}
                size="small"
                sx={{ minWidth: 'auto', px: 2, py: 0.75, textTransform: 'none', fontSize: '0.875rem' }}
              >
                {t('common.save', 'Save')}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={!selectedRentalType || isSystemDefault}
                size="small"
                sx={{ minWidth: 'auto', px: 2, py: 0.75, textTransform: 'none', fontSize: '0.875rem' }}
              >
                {t('common.delete', 'Delete')}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 0 }}>
              <Toolbar
                sx={{
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                  minHeight: '48px !important',
                  py: 0.5,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<Refresh fontSize="small" />}
                  onClick={() => refetch()}
                  size="small"
                  sx={{ minWidth: 'auto', px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.75rem' }}
                >
                  {t('common.refresh', 'Refresh')}
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                    disabled={page === 0}
                    sx={{ width: '28px', height: '28px', '&.Mui-disabled': { opacity: 0.3 } }}
                  >
                    <ArrowUpward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                  </IconButton>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', minWidth: '96px', textAlign: 'center' }}>
                    {sortedRentalTypes.length === 0
                      ? t('common.noResults', 'No results')
                      : `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, sortedRentalTypes.length)} ${t('common.of')} ${sortedRentalTypes.length}`}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={page >= totalPages - 1}
                    sx={{ width: '28px', height: '28px', '&.Mui-disabled': { opacity: 0.3 } }}
                  >
                    <ArrowDownward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                  </IconButton>
                  <Select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(0);
                    }}
                    size="small"
                    sx={{ height: '28px', fontSize: '0.8125rem', ml: 1, '& .MuiSelect-select': { py: 0.5, px: 1 } }}
                    renderValue={(value) => t('common.rowsPerPage', { count: value, defaultValue: `${value} / page` })}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                  </Select>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  size="small"
                  startIcon={<FilterListOff fontSize="small" />}
                  onClick={() => {
                    setColumnFilters({ name: '', kind: '', status: '' });
                    setSortConfig(null);
                    setPage(0);
                  }}
                  sx={{ fontSize: '0.8125rem', height: '28px', px: 1 }}
                >
                  {t('common.clearFilters')}
                </Button>
              </Toolbar>

              <TableContainer>
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', '& .MuiTableCell-root': { fontSize: '0.875rem', px: { xs: 2, sm: 3 } } }}>
                  <TableHead>
                    <TableRow>
                      <TableFilterCell
                        placeholder={t('rentalTypes.fields.name', 'Name')}
                        value={columnFilters.name}
                        onChange={(value) => handleColumnFilterChange('name', value)}
                        onSort={() => handleSort('name')}
                        sortDirection={sortConfig?.key === 'name' ? sortConfig.direction : null}
                        width="30%"
                      />
                      <TableFilterCell
                        placeholder={t('rentalTypes.fields.kind', 'Kind')}
                        value={columnFilters.kind}
                        onChange={(value) => handleColumnFilterChange('kind', value)}
                        onSort={() => handleSort('kind')}
                        sortDirection={sortConfig?.key === 'kind' ? sortConfig.direction : null}
                        width="20%"
                      />
                      <TableFilterCell
                        placeholder={t('fields.status')}
                        value={columnFilters.status}
                        onChange={(value) => handleColumnFilterChange('status', value)}
                        onSort={() => handleSort('status')}
                        sortDirection={sortConfig?.key === 'status' ? sortConfig.direction : null}
                        width="20%"
                      />
                      <TableCell width="20%">{t('rentalTypes.fields.default', 'Default')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : paginatedRentalTypes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          {t('rentalTypes.empty', 'No rental types found.')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRentalTypes.map((rentalType) => (
                        <TableRow
                          key={rentalType.id}
                          hover
                          selected={selectedRentalType?.id === rentalType.id}
                          onClick={() => handleRowClick(rentalType)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell sx={{ py: 0.75 }}>{rentalType.name}</TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            {rentalType.kind === 'PERSON' ? t('rentalTypes.kinds.person', 'Person') : t('rentalTypes.kinds.company', 'Company')}
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Chip
                              label={rentalType.active ? t('status.active') : t('status.inactive')}
                              size="small"
                              color={rentalType.active ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            {rentalType.isSystemDefault ? (
                              <Chip label={t('rentalTypes.systemDefault', 'Default')} size="small" color="info" variant="outlined" />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {(isCreating || selectedRentalType) && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ mt: 2, p: 1.25, ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6">
                    {isCreating ? t('rentalTypes.newRentalType', 'New Rental Type') : selectedRentalType?.name}
                  </Typography>
                  {isLockedDefault && (
                    <Chip label={t('rentalTypes.protectedHint', 'Protected default - cannot be edited or deleted')} size="small" color="info" variant="outlined" />
                  )}
                  {isSystemDefault && !isLockedDefault && (
                    <Chip label={t('rentalTypes.companyDefaultHint', 'Default - saving creates your own editable version')} size="small" color="info" variant="outlined" />
                  )}
                </Box>
                <Divider sx={{ mb: 1.25 }} />

                <Grid container spacing={1.25}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('rentalTypes.fields.name', 'Name')}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      disabled={isLockedDefault}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small" disabled={isLockedDefault}>
                      <InputLabel>{t('rentalTypes.fields.kind', 'Kind')}</InputLabel>
                      <Select
                        label={t('rentalTypes.fields.kind', 'Kind')}
                        value={formData.kind}
                        onChange={(e) => setFormData({ ...formData, kind: e.target.value as 'PERSON' | 'COMPANY' })}
                      >
                        <MenuItem value="PERSON">{t('rentalTypes.kinds.person', 'Person')}</MenuItem>
                        <MenuItem value="COMPANY">{t('rentalTypes.kinds.company', 'Company')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(formData.active)}
                          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                          disabled={isLockedDefault}
                        />
                      }
                      label={t('status.active')}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('rentalTypes.deleteTitle', 'Delete Rental Type')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('rentalTypes.deleteBody', {
              defaultValue: 'Delete "{{name}}"?',
              name: selectedRentalType?.name || '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleteMutation.isLoading}>
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
